import { useEffect } from 'react'
import type { TypeCode } from '@mindtype/shared'
import { getTypeInterpretation, DIMENSION_INFO, TYPE_COLORS, GROUP_COLORS } from '@mindtype/shared'

interface TypeDetailModalProps {
  typeCode: string
  isOpen: boolean
  onClose: () => void
  isUserType?: boolean
}

export function TypeDetailModal({ typeCode, isOpen, onClose, isUserType = false }: TypeDetailModalProps) {
  const interpretation = getTypeInterpretation(typeCode as TypeCode)
  const colorConfig = TYPE_COLORS[typeCode as TypeCode]
  const groupColors = colorConfig ? GROUP_COLORS[colorConfig.group] : null

  // ESC 键关闭
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEsc)
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.removeEventListener('keydown', handleEsc)
      document.body.style.overflow = ''
    }
  }, [isOpen, onClose])

  if (!isOpen || !interpretation || !colorConfig) return null

  // 解析类型代码的四个字母
  const dimensions = [
    { letter: typeCode[0], dimension: 'D1' as const },
    { letter: typeCode[1], dimension: 'D2' as const },
    { letter: typeCode[2], dimension: 'D3' as const },
    { letter: typeCode[3], dimension: 'D4' as const },
  ]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* 背景遮罩 */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* 模态框 */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* 头部 */}
        <div
          className="px-6 py-5"
          style={{ backgroundColor: colorConfig.primary, color: colorConfig.text }}
        >
          <div className="flex items-center justify-between">
            <div>
              {isUserType && (
                <span className="inline-block px-2 py-0.5 bg-white/20 rounded text-xs mb-2">
                  你的类型
                </span>
              )}
              <h2 className="text-2xl font-bold">{interpretation.name}</h2>
              <p className="mt-1" style={{ opacity: 0.8 }}>{interpretation.tagline}</p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-mono font-bold tracking-wider">
                {typeCode}
              </div>
            </div>
          </div>

          {/* 四维度字母解读 */}
          <div className="flex gap-2 mt-4">
            {dimensions.map(({ letter, dimension }) => {
              const dimInfo = DIMENSION_INFO[dimension]
              const pole = letter === dimInfo.poleA.letter ? dimInfo.poleA : dimInfo.poleB

              // 所有组的按钮都用白色文字
              const textColor = '#ffffff'

              return (
                <div
                  key={dimension}
                  className="flex-1 rounded-lg px-3 py-2 text-center"
                  style={{ backgroundColor: groupColors?.secondary }}
                >
                  <div className="text-lg font-bold" style={{ color: textColor }}>{letter}</div>
                  <div className="text-xs" style={{ color: textColor, opacity: 0.8 }}>{pole.name}</div>
                </div>
              )
            })}
          </div>
        </div>

        {/* 内容区域 */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* 类型描述 */}
          <div>
            <p className="text-gray-700 leading-relaxed">{interpretation.description}</p>
          </div>

          {/* 核心特质 */}
          <div>
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: colorConfig.primary }}>
              核心特质
            </h3>
            <div className="flex flex-wrap gap-2">
              {interpretation.coreTraits.map((trait, i) => (
                <span
                  key={i}
                  className="px-3 py-1.5 rounded-full text-sm"
                  style={{ backgroundColor: `${colorConfig.primary}15`, color: colorConfig.primary }}
                >
                  {trait}
                </span>
              ))}
            </div>
          </div>

          {/* 优势与挑战 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-xl p-4" style={{ backgroundColor: `${colorConfig.primary}10` }}>
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: colorConfig.primary }}>
                优势
              </h3>
              <ul className="space-y-2">
                {interpretation.strengths.map((item, i) => (
                  <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                    <span style={{ color: colorConfig.primary }} className="mt-0.5">•</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl p-4" style={{ backgroundColor: `${colorConfig.secondary}15` }}>
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: colorConfig.primary }}>
                潜在挑战
              </h3>
              <ul className="space-y-2">
                {interpretation.challenges.map((item, i) => (
                  <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                    <span style={{ color: colorConfig.secondary }} className="mt-0.5">•</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* 理想环境 */}
          <div className="rounded-xl p-4" style={{ backgroundColor: `${colorConfig.primary}10` }}>
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: colorConfig.primary }}>
              理想环境
            </h3>
            <ul className="space-y-2">
              {interpretation.idealEnvironment.map((item, i) => (
                <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                  <span style={{ color: colorConfig.primary }} className="mt-0.5">•</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* 压力反应 */}
          <div className="rounded-xl p-4" style={{ backgroundColor: `${colorConfig.secondary}15` }}>
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: colorConfig.primary }}>
              压力下的反应
            </h3>
            <p className="text-sm text-gray-700">{interpretation.stressResponse}</p>
          </div>

          {/* 成长建议 */}
          <div
            className="rounded-xl p-4"
            style={{ background: `linear-gradient(135deg, ${colorConfig.primary}10, ${colorConfig.secondary}15)` }}
          >
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: colorConfig.primary }}>
              成长建议
            </h3>
            <ul className="space-y-2">
              {interpretation.growthTips.map((tip, i) => (
                <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                  <span
                    className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0"
                    style={{ backgroundColor: `${colorConfig.primary}25`, color: colorConfig.primary }}
                  >
                    {i + 1}
                  </span>
                  {tip}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* 底部关闭按钮 */}
        <div className="border-t p-4" style={{ borderColor: `${colorConfig.primary}20` }}>
          <button
            onClick={onClose}
            className="w-full py-3 rounded-xl font-medium transition-opacity hover:opacity-90"
            style={{ backgroundColor: colorConfig.primary, color: colorConfig.text }}
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  )
}
